/**
 * Child Management Components
 * Contains modals for adding, editing, and deleting children
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import boyAvatar1 from '../../assets/child-avatars/boy-1.svg';
import boyAvatar2 from '../../assets/child-avatars/boy-2.svg';
import girlAvatar1 from '../../assets/child-avatars/girl-1.svg';
import girlAvatar2 from '../../assets/child-avatars/girl-2.svg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_AVATARS = {
  boy: [boyAvatar1, boyAvatar2],
  girl: [girlAvatar1, girlAvatar2]
};

const ALL_PRESET_AVATARS = [...PRESET_AVATARS.boy, ...PRESET_AVATARS.girl];

const CROP_SIZE = 256; // px – the circular crop area

/**
 * Canvas-based image cropper with zoom and pan support.
 * Renders a fixed circular crop window; the parent image can be
 * dragged (pan) and scaled (zoom slider). On confirm the cropped
 * JPEG blob is passed to onCrop.
 */
const ImageCropper = ({ imageSrc, onCrop, onCancel }) => {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const startDrag = (clientX, clientY) => {
    setDragStart({ x: clientX - offset.x, y: clientY - offset.y });
  };
  const moveDrag = (clientX, clientY) => {
    if (!dragStart) return;
    setOffset({ x: clientX - dragStart.x, y: clientY - dragStart.y });
  };
  const endDrag = () => setDragStart(null);

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img) return;

    const canvas = document.createElement('canvas');
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;
    const ctx = canvas.getContext('2d');

    // Circular clip
    ctx.beginPath();
    ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Where is the top-left of the image inside the container?
    const imgDisplayLeft = (CROP_SIZE - img.naturalWidth * zoom) / 2 + offset.x;
    const imgDisplayTop  = (CROP_SIZE - img.naturalHeight * zoom) / 2 + offset.y;

    // Corresponding source rect in original image coordinates
    const srcX = -imgDisplayLeft / zoom;
    const srcY = -imgDisplayTop  / zoom;
    const srcW =  CROP_SIZE / zoom;
    const srcH =  CROP_SIZE / zoom;

    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, CROP_SIZE, CROP_SIZE);

    canvas.toBlob((blob) => blob && onCrop(blob), 'image/jpeg', 0.92);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl p-4 w-72 shadow-2xl">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 text-center">Adjust & Crop Photo</h3>

        {/* Crop container — the circle IS the crop area */}
        <div
          ref={containerRef}
          className="relative mx-auto overflow-hidden rounded-full bg-gray-200"
          style={{
            width: CROP_SIZE,
            height: CROP_SIZE,
            cursor: dragStart ? 'grabbing' : 'grab',
          }}
          onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
          onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={(e) => { const t = e.touches[0]; startDrag(t.clientX, t.clientY); }}
          onTouchMove={(e) => { const t = e.touches[0]; moveDrag(t.clientX, t.clientY); }}
          onTouchEnd={endDrag}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Crop preview"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
              transformOrigin: 'center',
              userSelect: 'none',
              pointerEvents: 'none',
              maxWidth: 'none',
            }}
            draggable={false}
          />
        </div>

        {/* Zoom slider */}
        <div className="mt-4 px-1">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Zoom</span>
            <span>{Math.round(zoom * 100)}%</span>
          </div>
          <input
            type="range"
            min="0.5"
            max="4"
            step="0.05"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="w-full accent-blue-600"
          />
          <p className="text-xs text-gray-400 text-center mt-1">Drag to pan · Slider to zoom</p>
        </div>

        <div className="flex space-x-3 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCrop}
            className="flex-1 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Use Photo
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Edit Child Modal Component
 * Allows editing child details and photo upload
 */
export const EditChildModal = ({ child, onClose, onChildUpdated, onRequestDelete }) => {
  const [formData, setFormData] = useState({
    name: child?.name || '',
    age: child?.age?.toString() || '',
    grade: child?.grade?.toString() || '',
    school_name: child?.school_name || '',
    school_id: child?.school_id || '',
    board: child?.board || '',
    curriculum_available: child?.curriculum_available || '',
    photo_url: child?.photo_url || ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedGender, setSelectedGender] = useState('boy');
  const [presetIndex, setPresetIndex] = useState(0);
  const [photoMode, setPhotoMode] = useState(() => (
    ALL_PRESET_AVATARS.includes(child?.photo_url) ? 'preset' : 'upload'
  ));
  const [showPhotoOptions, setShowPhotoOptions] = useState(() => !Boolean(child?.photo_url));
  const [cropperSrc, setCropperSrc] = useState(null);

  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  const [selectedSchoolFromDb, setSelectedSchoolFromDb] = useState(Boolean(child?.school_id));
  const [shouldSearchSchools, setShouldSearchSchools] = useState(false);
  const isSchoolLocked = Boolean(child?.has_school_relationship);

  const avatarOptions = useMemo(() => PRESET_AVATARS[selectedGender], [selectedGender]);
  const hasPhoto = Boolean(photoPreview || formData.photo_url || selectedPhoto);

  useEffect(() => {
    if (!hasPhoto) {
      setShowPhotoOptions(true);
    }
  }, [hasPhoto]);

  useEffect(() => {
    if (!shouldSearchSchools) {
      return;
    }

    const trimmedQuery = schoolQuery.trim();
    if (trimmedQuery.length < 2) {
      setSchoolSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setSchoolSearchLoading(true);
      try {
        const response = await axios.get('/schools/search', {
          params: { query: trimmedQuery, limit: 8 },
          signal: controller.signal
        });
        setSchoolSuggestions(response.data.schools || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error searching schools:', error);
        }
      } finally {
        setSchoolSearchLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [schoolQuery, shouldSearchSchools]);

  const handleSchoolInput = (value) => {
    setFormData((prev) => ({
      ...prev,
      school_name: value,
      school_id: '',
      curriculum_available: ''
    }));
    setSchoolQuery(value);
    setShouldSearchSchools(true);
    setSelectedSchoolFromDb(false);
  };

  const handleSelectSchool = (school) => {
    setFormData((prev) => ({
      ...prev,
      school_name: school.name,
      school_id: school.id || '',
      board: school.board || prev.board,
      curriculum_available: school.curriculum_available || ''
    }));
    setSchoolQuery('');
    setSchoolSuggestions([]);
    setShouldSearchSchools(false);
    setSelectedSchoolFromDb(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedPhotoUrl = formData.photo_url;

      if (selectedPhoto) {
        setUploadingPhoto(true);
        const uploadData = new FormData();
        uploadData.append('file', selectedPhoto);

        const uploadResponse = await axios.post(`/children/${child.id}/upload-photo`, uploadData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        updatedPhotoUrl = uploadResponse.data.photo_url;
      }

      await axios.put(`/children/${child.id}`, {
        name: formData.name,
        age: parseInt(formData.age),
        grade: parseInt(formData.grade),
        school_name: formData.school_name,
        school_id: formData.school_id || '',
        board: formData.board,
        curriculum_available: formData.curriculum_available || null,
        photo_url: updatedPhotoUrl
      });
      
      setFormData(prev => ({ ...prev, photo_url: updatedPhotoUrl }));
      setSelectedPhoto(null);
      setPhotoPreview(null);

      onChildUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating child:', error);
      alert('Error updating child. Please try again.');
    } finally {
      setLoading(false);
      setUploadingPhoto(false);
    }
    setLoading(false);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Open cropper so user can zoom/pan before confirming
    const reader = new FileReader();
    reader.onloadend = () => setCropperSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (blob) => {
    const croppedFile = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    setSelectedPhoto(croppedFile);
    setPhotoPreview(URL.createObjectURL(blob));
    setFormData((prev) => ({ ...prev, photo_url: '' }));
    setCropperSrc(null);
  };

  const cyclePreset = (direction) => {
    setPresetIndex((prev) => (prev + direction + avatarOptions.length) % avatarOptions.length);
  };

  const handlePresetSelect = (indexOverride) => {
    const chosenIndex = typeof indexOverride === 'number' ? indexOverride : presetIndex;
    const chosenUrl = avatarOptions[chosenIndex];

    setPresetIndex(chosenIndex);
    setFormData((prev) => ({ ...prev, photo_url: chosenUrl }));
    setPhotoPreview(chosenUrl);
    setSelectedPhoto(null);
    setPhotoMode('preset');
  };

  return (
    <>
    {cropperSrc && (
      <ImageCropper
        imageSrc={cropperSrc}
        onCrop={handleCropComplete}
        onCancel={() => setCropperSrc(null)}
      />
    )}
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all absolute top-4 right-4"
          aria-label="Close"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <h2 className="text-xl font-semibold mb-4">Edit Child Details</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo Upload Section */}
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center mb-3">
              {photoPreview || formData.photo_url ? (
                <img
                  src={(photoPreview || formData.photo_url).startsWith('/api/')
                    ? `${BACKEND_URL}${photoPreview || formData.photo_url}`
                    : (photoPreview || formData.photo_url)}
                  alt={formData.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-white font-semibold text-xl">${formData.name.charAt(0)}</span>`;
                  }}
                />
              ) : (
                <span className="text-white font-semibold text-xl">{formData.name.charAt(0)}</span>
              )}
            </div>
            {hasPhoto && !showPhotoOptions && (
              <button
                type="button"
                onClick={() => setShowPhotoOptions(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Edit photo
              </button>
            )}
            {showPhotoOptions && hasPhoto && (
              <button
                type="button"
                onClick={() => setShowPhotoOptions(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Hide photo options
              </button>
            )}
            {showPhotoOptions && (
            <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">Choose a photo</p>
                <div className="flex space-x-2">
                  {['upload', 'preset'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setPhotoMode(mode)}
                      className={`px-2 py-1 rounded-md text-xs font-medium border ${photoMode === mode ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                      {mode === 'upload' ? 'Upload' : 'Preset'}
                    </button>
                  ))}
                </div>
              </div>

              {photoMode === 'upload' ? (
                <div className="text-center">
                  <label className="cursor-pointer inline-flex items-center space-x-2 bg-white px-3 py-2 rounded-md border border-gray-300 shadow-sm hover:shadow-md transition-shadow">
                    <span className="text-sm font-medium text-gray-700">{selectedPhoto ? 'Change photo (optional)' : 'Add photo (optional)'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoSelect}
                      className="hidden"
                      disabled={uploadingPhoto || loading}
                    />
                  </label>
                  {selectedPhoto && (
                    <p className="text-xs text-gray-500 mt-1">{selectedPhoto.name} ({(selectedPhoto.size / 1024).toFixed(0)}KB)</p>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-600">Choose a style</p>
                    <div className="flex space-x-2">
                      {['boy', 'girl'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => { setSelectedGender(option); setPresetIndex(0); }}
                          className={`px-2 py-1 rounded-md text-[11px] font-medium border ${selectedGender === option ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                        >
                          {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-center space-x-3">
                    <button
                      type="button"
                      onClick={() => cyclePreset(-1)}
                      className="compact-action-btn w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-white"
                      aria-label="Previous preset"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePresetSelect()}
                      className={`relative w-20 h-20 rounded-full overflow-hidden border-2 ${formData.photo_url === avatarOptions[presetIndex] ? 'border-blue-600' : 'border-transparent'} shadow-sm hover:shadow-md transition-shadow`}
                      aria-label="Select preset"
                    >
                      <img src={avatarOptions[presetIndex]} alt="Preset avatar" className="w-full h-full object-cover" />
                      {formData.photo_url === avatarOptions[presetIndex] && (
                        <span className="absolute inset-x-2 bottom-2 bg-blue-600 text-white text-[11px] rounded-full px-2 py-[2px] text-center">Selected</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => cyclePreset(1)}
                      className="compact-action-btn w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-white"
                      aria-label="Next preset"
                    >
                      →
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">Click the image to use it as the child's photo.</p>
                </div>
              )}
            </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Child's Name</strong></label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="edit-child-name-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Age</strong></label>
              <input
                type="number"
                min="3"
                max="18"
                required
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="edit-child-age-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Grade</strong></label>
              <select
                required
                value={formData.grade}
                onChange={(e) => setFormData({...formData, grade: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isSchoolLocked ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' : 'border-gray-300'}`}
                data-testid="edit-child-grade-select"
                disabled={isSchoolLocked}
              >
                <option value="">Select Grade</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>Grade {i + 1}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1"><strong>School Name</strong></label>
            <div className="relative">
              <input
                type="text"
                required
                value={formData.school_name}
                onChange={(e) => handleSchoolInput(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${isSchoolLocked ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' : 'border-gray-300'}`}
                placeholder="Search school name, address, or district"
                data-testid="edit-child-school-input"
                disabled={isSchoolLocked}
              />
              {schoolSearchLoading && (
                <div className="absolute right-3 top-3 text-xs text-gray-500">Searching...</div>
              )}
              {schoolSuggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {schoolSuggestions.map((school) => (
                    <button
                      type="button"
                      key={school.id}
                      onMouseDown={() => handleSelectSchool(school)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50"
                    >
                      <div className="font-medium text-gray-800">{school.name}</div>
                      <div className="text-xs text-gray-600">
                        {[school.address, school.district, school.state].filter(Boolean).join(', ')}
                      </div>
                      <div className="text-xs text-blue-700 font-semibold">{school.board || 'Board not set'}</div>
                    </button>
                  ))}
                </div>
              )}
              {isSchoolLocked ? (
                <p className="text-xs text-gray-500 mt-1">
                  School is linked through the school space and cannot be changed.
                </p>
              ) : selectedSchoolFromDb ? (
                <p className="text-xs text-blue-700 mt-1">
                  School selected from the database. Board is auto-selected.
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Search by school name, address, district, or city. Selecting a listed school auto-fills its board.
                </p>
              )}
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Board</strong></label>
              {selectedSchoolFromDb && (
                <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-[2px]">Auto-selected</span>
              )}
            </div>
            <select
              required
              value={formData.board}
              onChange={(e) => setFormData({...formData, board: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${selectedSchoolFromDb ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' : 'border-gray-300'}`}
              data-testid="edit-child-board-select"
              disabled={selectedSchoolFromDb}
            >
              <option value="">Select Board</option>
              <option value="CBSE">CBSE (Central Board of Secondary Education)</option>
              <option value="ICSE">ICSE (Indian Certificate of Secondary Education)</option>
              <option value="IB">IB (International Baccalaureate)</option>
              <option value="Cambridge">Cambridge International</option>
              <option value="State">State Board</option>
            </select>
            {selectedSchoolFromDb && (
              <p className="text-xs text-gray-500 mt-1">
                Board has been auto-selected from the chosen school and cannot be changed. Select a school not in the database to edit the board.
              </p>
            )}
          </div>
          
          <div className="flex flex-col gap-3 pt-4">
            <div className="flex space-x-4 text-xs sm:text-sm">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onRequestDelete && child) {
                    onClose();
                    onRequestDelete(child);
                  }
                }}
                className="flex-1 py-2 px-4 bg-red-500 border border-red-600 text-white rounded-lg hover:bg-red-600"
                data-testid={child ? `delete-child-${child.id}` : undefined}
              >
                Delete
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="update-child-submit"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

/**
 * Delete Confirmation Modal Component
 * Shows confirmation before deleting a child
 */
export const DeleteConfirmModal = ({ child, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Child</h3>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete <strong>{child?.name}</strong>? This action cannot be undone.
          </p>
          <div className="flex space-x-4">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700"
              data-testid="confirm-delete-child"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Add Child Modal Component
 * Form for adding a new child with optional photo and access code
 */
export const AddChildModal = ({ onClose, onChildAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    grade: '',
    school_name: '',
    school_id: '',
    board: '',
    curriculum_available: '',
    access_code: '',  // Optional - if empty, backend will generate random code
    photo_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedGender, setSelectedGender] = useState('boy');
  const [presetIndex, setPresetIndex] = useState(0);
  const [photoMode, setPhotoMode] = useState('upload');
  const [cropperSrc, setCropperSrc] = useState(null);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  const [selectedSchoolFromDb, setSelectedSchoolFromDb] = useState(false);

  const avatarOptions = useMemo(() => PRESET_AVATARS[selectedGender], [selectedGender]);

  useEffect(() => {
    const trimmedQuery = schoolQuery.trim();
    if (trimmedQuery.length < 2) {
      setSchoolSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      setSchoolSearchLoading(true);
      try {
        const response = await axios.get('/schools/search', {
          params: { query: trimmedQuery, limit: 8 },
          signal: controller.signal
        });
        setSchoolSuggestions(response.data.schools || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error searching schools:', error);
        }
      } finally {
        setSchoolSearchLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [schoolQuery]);

  const handleSchoolInput = (value) => {
    setFormData((prev) => ({
      ...prev,
      school_name: value,
      school_id: '',
      curriculum_available: ''
    }));
    setSchoolQuery(value);
    setSelectedSchoolFromDb(false);
  };

  const handleSelectSchool = (school) => {
    setFormData((prev) => ({
      ...prev,
      school_name: school.name,
      school_id: school.id || '',
      board: school.board || prev.board,
      curriculum_available: school.curriculum_available || ''
    }));
    setSchoolQuery(school.name);
    setSchoolSuggestions([]);
    setSelectedSchoolFromDb(true);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Open cropper so user can zoom/pan before confirming
    const reader = new FileReader();
    reader.onloadend = () => setCropperSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (blob) => {
    const croppedFile = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    setSelectedPhoto(croppedFile);
    setPhotoPreview(URL.createObjectURL(blob));
    setFormData((prev) => ({ ...prev, photo_url: '' }));
    setPhotoMode('upload');
    setCropperSrc(null);
  };

  const cyclePreset = (direction) => {
    setPresetIndex((prev) => (prev + direction + avatarOptions.length) % avatarOptions.length);
  };

  const handlePresetSelect = (indexOverride) => {
    const chosenIndex = typeof indexOverride === 'number' ? indexOverride : presetIndex;
    const chosenUrl = avatarOptions[chosenIndex];

    setPresetIndex(chosenIndex);
    setFormData((prev) => ({ ...prev, photo_url: chosenUrl }));
    setPhotoPreview(chosenUrl);
    setSelectedPhoto(null);
    setPhotoMode('preset');
  };

  const uploadPhotoInChunks = async (childId, file) => {
    // For files under 1MB, use direct upload (no chunking needed)
    if (file.size < 1024 * 1024) {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`/children/${childId}/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    }

    // For larger files, implement chunked upload
    const chunkSize = 512 * 1024; // 512KB chunks
    const totalChunks = Math.ceil(file.size / chunkSize);
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      const chunkFormData = new FormData();
      chunkFormData.append('file', chunk);
      chunkFormData.append('chunk_index', i.toString());
      chunkFormData.append('total_chunks', totalChunks.toString());
      chunkFormData.append('filename', file.name);
      
      await axios.post(`/children/${childId}/upload-photo`, chunkFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    return { message: 'Photo uploaded successfully' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Step 1: Create the child
      const response = await axios.post('/children', {
        name: formData.name,
        age: parseInt(formData.age),
        grade: parseInt(formData.grade),
        school_name: formData.school_name,
        school_id: formData.school_id || '',
        board: formData.board,
        curriculum_available: formData.curriculum_available || null,
        photo_url: formData.photo_url || undefined,
        access_code: formData.access_code.trim() || null  // Send null if empty for auto-generation
      });
      
      const newChildId = response.data.child.id;
      
      // Step 2: Upload photo if selected
      if (selectedPhoto) {
        setUploadingPhoto(true);
        try {
          await uploadPhotoInChunks(newChildId, selectedPhoto);
        } catch (photoError) {
          console.error('Error uploading photo:', photoError);
          // Don't fail the whole operation if photo upload fails
          alert('Child added but photo upload failed. You can add the photo later from Edit Child.');
        }
        setUploadingPhoto(false);
      }
      
      // Show success message with access code info
      if (response.data.access_code_generated) {
        alert(`Child added successfully! Generated access code: ${response.data.child.access_code}`);
      } else {
        alert('Child added successfully!');
      }
      
      onChildAdded();
      onClose();
    } catch (error) {
      console.error('Error adding child:', error);
      alert('Error adding child. Please try again.');
    }
    setLoading(false);
  };

  return (
    <>
    {cropperSrc && (
      <ImageCropper
        imageSrc={cropperSrc}
        onCrop={handleCropComplete}
        onCancel={() => setCropperSrc(null)}
      />
    )}
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
        <button
          type="button"
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all absolute top-4 right-4"
          aria-label="Close"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        <h2 className="text-xl font-semibold mb-4">Add Child</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Photo Upload Section */}
          <div className="text-center">
            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center mb-3">
              {photoPreview ? (
                <img 
                  src={photoPreview} 
                  alt="Child preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-semibold text-xl">
                  {formData.name ? formData.name.charAt(0).toUpperCase() : '+'}
                </span>
              )}
            </div>
            <label className="cursor-pointer">
              <span className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                {selectedPhoto ? 'Change Photo (Optional)' : 'Add Photo (Optional)'}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </label>
            {selectedPhoto && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedPhoto.name} ({(selectedPhoto.size / 1024).toFixed(0)}KB)
              </p>
            )}
            <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Or pick a preset photo</p>
                <div className="flex space-x-2">
                  {['boy', 'girl'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => { setSelectedGender(option); setPresetIndex(0); }}
                      className={`px-2 py-1 rounded-md text-xs font-medium border ${selectedGender === option ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => cyclePreset(-1)}
                  className="compact-action-btn w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-white"
                  aria-label="Previous preset"
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetSelect()}
                  className={`relative w-20 h-20 rounded-full overflow-hidden border-2 ${formData.photo_url === avatarOptions[presetIndex] ? 'border-blue-600' : 'border-transparent'} shadow-sm hover:shadow-md transition-shadow`}
                  aria-label="Select preset"
                >
                  <img src={avatarOptions[presetIndex]} alt="Preset avatar" className="w-full h-full object-cover" />
                  {formData.photo_url === avatarOptions[presetIndex] && (
                    <span className="absolute inset-x-2 bottom-2 bg-blue-600 text-white text-[11px] rounded-full px-2 py-[2px] text-center">Selected</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => cyclePreset(1)}
                  className="compact-action-btn w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-white"
                  aria-label="Next preset"
                >
                  →
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">Click the image to use it as the child's photo.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Child's Name</strong></label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="child-name-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Age</strong></label>
              <input
                type="number"
                min="3"
                max="18"
                required
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="child-age-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Grade</strong></label>
              <select
                required
                value={formData.grade}
                onChange={(e) => setFormData({...formData, grade: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="child-grade-select"
              >
                <option value="">Select Grade</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>Grade {i + 1}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-blue-800 mb-1"><strong>School Name</strong></label>
            <input
              type="text"
              required
              value={formData.school_name}
              onChange={(e) => handleSchoolInput(e.target.value)}
              onFocus={() => setSchoolQuery(formData.school_name)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Search school name, address, or district"
              data-testid="child-school-input"
            />
            {schoolSearchLoading && (
              <div className="absolute right-3 top-9 text-xs text-gray-500">Searching...</div>
            )}
            {schoolSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {schoolSuggestions.map((school) => (
                  <button
                    type="button"
                    key={school.id}
                    onMouseDown={() => handleSelectSchool(school)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50"
                  >
                    <div className="font-medium text-gray-800">{school.name}</div>
                    <div className="text-xs text-gray-600">
                      {[school.address, school.district, school.state].filter(Boolean).join(', ')}
                    </div>
                    <div className="text-xs text-blue-700 font-semibold">{school.board || 'Board not set'}</div>
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">Search by school name, address, district, or city. Selecting a listed school auto-fills its board.</p>
          </div>
          
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-blue-800 mb-1"><strong>Board</strong></label>
              {selectedSchoolFromDb && (
                <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-[2px]">Auto-selected from database</span>
              )}
            </div>
            <select
              required
              value={formData.board}
              onChange={(e) => setFormData({...formData, board: e.target.value})}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${selectedSchoolFromDb ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' : 'border-gray-300'}`}
              data-testid="child-board-select"
              disabled={selectedSchoolFromDb}
            >
              <option value="">Select Board</option>
              <option value="CBSE">CBSE (Central Board of Secondary Education)</option>
              <option value="ICSE">ICSE (Indian Certificate of Secondary Education)</option>
              <option value="IB">IB (International Baccalaureate)</option>
              <option value="Cambridge">Cambridge International</option>
              <option value="State">State Board</option>
            </select>
            {selectedSchoolFromDb && (
              <p className="text-xs text-gray-500 mt-1">Board has been auto-selected from the chosen school and cannot be changed.</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-1">
              <strong>Access Code (Optional)</strong>
            </label>
            <input
              type="text"
              value={formData.access_code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4); // Only digits, max 4
                setFormData({...formData, access_code: value});
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="4-digit code (leave empty to auto-generate)"
              maxLength="4"
              pattern="[0-9]{4}"
              data-testid="child-access-code-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to automatically generate a random 4-digit code
            </p>
          </div>
          
          <div className="flex space-x-4 text-xs sm:text-sm">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading || uploadingPhoto}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingPhoto}
              className="flex-1 py-2 px-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="add-child-submit"
            >
              {uploadingPhoto ? 'Uploading Photo...' : loading ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};
